import ajax from '@ep/tools/ajax.js';
import toast from '@ep/tools/toast.js';
import createElement from '@ep/components/create/element.js';

const TARGET = Object.freeze({
  scope: 'shared',
  service: 'schedule',
  domain: 'scheduling',
  module: 'schedules',
});

const dictionary = () => globalThis.dic?.systemSchedules || {};
const text = (path, fallback = '') => {
  let cursor = dictionary();
  for (const key of String(path || '').split('.').filter(Boolean)) cursor = cursor?.[key];
  return typeof cursor === 'string' ? cursor : fallback;
};

const post = (method, body = {}) => ajax.post({
  ...TARGET,
  method,
  body,
  config: { loader: false },
});

const refresh = async (component) => {
  if (component && typeof component.render === 'function') await component.render();
};

const runMutation = async ({ method, id, successPath, component, extra = {} }) => {
  try {
    const result = await post(method, { id, ...extra });
    if (result?.status !== 'success') throw new Error(result?.code || 'SYSTEM_SCHEDULE_OPERATION_FAILED');
    toast({ type: 'success', message: text(successPath) });
    await refresh(component);
    return result;
  } catch (error) {
    console.error('[systemSchedules] ação falhou:', method, error?.message || error);
    toast({ type: 'error', message: text('messages.operationError') });
    return null;
  }
};

const openHistory = async ({ record }) => {
  try {
    const result = await post('getHistory', { id: record.id, limit: 50 });
    if (result?.status !== 'success') throw new Error(result?.code || 'SYSTEM_SCHEDULE_HISTORY_FAILED');
    const history = Array.isArray(result?.data) ? result.data : [];

    await createElement({
      element: 'div',
      parent: 'body',
      dialog: {
        modal: true,
        variant: 'drawer',
        placement: 'right',
        effect: 'slide-right',
        width: '56rem',
        title: `${text('history.title')} · ${record.name || ''}`,
        autoOpen: true,
        kill: true,
      },
      content: history.length > 0
        ? [{
          element: 'datagrid',
          data: history,
          search: false,
          displayRecords: false,
          pagination: false,
          pageLength: 'all',
          actions: [],
          header: [[
            { id: 'triggerType', text: text('fields.triggerType'), dic: 'systemScheduleTrigger' },
            { id: 'plannedAt', text: text('fields.plannedAt'), mask: { template: 'dateTime' } },
            { id: 'status', text: text('fields.status'), dic: 'systemScheduleRunStatus', mask: { template: 'tag' } },
            { id: 'currentAttempt', text: text('fields.currentAttempt') },
            { id: 'finishedAt', text: text('fields.finishedAt'), mask: { template: 'dateTime' } },
            { id: 'errorCode', text: text('fields.errorCode') },
          ]],
        }, {
          tag: 'p',
          text: text('history.limitNotice'),
        }]
        : [{
          tag: 'p',
          text: text('history.empty'),
        }],
    });
  } catch (error) {
    console.error('[systemSchedules] histórico falhou:', error?.message || error);
    toast({ type: 'error', message: text('messages.historyError') });
  }
};

const actionRegistry = () => ({
  scheduleHistory: {
    label: text('actions.history'),
    tooltip: text('actions.historyTooltip'),
    icon: { type: 'solid', name: 'clock-rotate-left' },
    visible: ({ record }) => Boolean(record?.id),
    enabled: ({ record }) => Boolean(record?.id),
    run: openHistory,
  },
  scheduleRunNow: {
    label: text('actions.runNow'),
    tooltip: text('actions.runNowTooltip'),
    icon: { type: 'solid', name: 'play' },
    visible: ({ record }) => ['active', 'paused', 'failed'].includes(String(record?.status || '').toLowerCase()),
    enabled: ({ record }) => ['active', 'paused', 'failed'].includes(String(record?.status || '').toLowerCase()),
    confirm: {
      title: text('confirmations.runNowTitle'),
      message: text('confirmations.runNow'),
    },
    run: ({ record, component }) => runMutation({
      method: 'runNow',
      id: record.id,
      successPath: 'messages.runNowQueued',
      component,
      extra: { idempotencyKey: globalThis.crypto?.randomUUID?.() || String(Date.now()) },
    }),
  },
  schedulePause: {
    label: text('actions.pause'),
    tooltip: text('actions.pauseTooltip'),
    icon: { type: 'solid', name: 'pause' },
    visible: ({ record }) => ['active', 'failed'].includes(String(record?.status || '').toLowerCase()),
    enabled: ({ record }) => ['active', 'failed'].includes(String(record?.status || '').toLowerCase()),
    confirm: {
      title: text('confirmations.pauseTitle'),
      message: text('confirmations.pause'),
    },
    run: ({ record, component }) => runMutation({
      method: 'pause',
      id: record.id,
      successPath: 'messages.paused',
      component,
    }),
  },
  scheduleResume: {
    label: text('actions.resume'),
    tooltip: text('actions.resumeTooltip'),
    icon: { type: 'solid', name: 'rotate-right' },
    visible: ({ record }) => String(record?.status || '').toLowerCase() === 'paused',
    enabled: ({ record }) => String(record?.status || '').toLowerCase() === 'paused',
    confirm: {
      title: text('confirmations.resumeTitle'),
      message: text('confirmations.resume'),
    },
    run: ({ record, component }) => runMutation({
      method: 'resume',
      id: record.id,
      successPath: 'messages.resumed',
      component,
    }),
  },
});

export function installSystemScheduleActions() {
  globalThis.__EP_DATAGRID_ACTION_REGISTRY = {
    ...(globalThis.__EP_DATAGRID_ACTION_REGISTRY || {}),
    ...actionRegistry(),
  };
}
